import { useEffect, useState } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';

// Polymarket bridge 提现报价：按收款地址/目标链/目标币/金额估算网络费与实际到账。
// 经本项目 /api/polymarket/quote 代理转发到 https://bridge.polymarket.com/quote（公开路径，无需鉴权）
const QUOTE_URL = '/api/polymarket/quote';

/** 系统钱包资金所在链/币：Polygon 上的 USDC.e（Polymarket 结算币） */
export const WITHDRAW_FROM_CHAIN_ID = '137';
export const WITHDRAW_FROM_TOKEN_ADDRESS = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';
export const WITHDRAW_FROM_TOKEN_DECIMALS = 6;

/** 非 EVM 链 id（Polymarket bridge 定义） */
export const SOLANA_CHAIN_ID = '1151111081099710';
export const TRON_CHAIN_ID = '728126428';
export const BITCOIN_CHAIN_ID = '8253038';

const BASE58_RE = /^[1-9A-HJ-NP-Za-km-z]+$/;

/**
 * 收款地址基本格式校验（按目标链区分）。
 * EVM 校验 0x + 40 位 hex（不含 checksum）；Solana/Tron 校验 base58 与长度；
 * Bitcoin 支持 bech32（bc1...）与 legacy/P2SH（1.../3...）。
 */
export function isValidRecipientAddress(address: string, chainId: string | number): boolean {
  const value = address.trim();
  if (!value) return false;
  switch (String(chainId)) {
    case SOLANA_CHAIN_ID:
      return value.length >= 32 && value.length <= 44 && BASE58_RE.test(value);
    case TRON_CHAIN_ID:
      return value.length === 34 && value.startsWith('T') && BASE58_RE.test(value);
    case BITCOIN_CHAIN_ID:
      return (
        /^bc1[02-9ac-hj-np-z]{11,87}$/.test(value.toLowerCase()) ||
        /^[13][1-9A-HJ-NP-Za-km-z]{25,34}$/.test(value)
      );
    default:
      return /^0x[0-9a-fA-F]{40}$/.test(value);
  }
}

export interface WithdrawQuoteFeeBreakdown {
  appFeeLabel: string;
  appFeePercent: number;
  appFeeUsd: number;
  fillCostPercent: number;
  fillCostUsd: number;
  /** 网络 gas 费（美元） */
  gasUsd: number;
  maxSlippage: number;
  /** 扣除滑点后的最低到账数量（目标币单位） */
  minReceived: number;
  swapImpact: number;
  swapImpactUsd: number;
  totalImpact: number;
  totalImpactUsd: number;
}

export interface WithdrawQuoteResponse {
  /** 预计到账耗时（毫秒） */
  estCheckoutTimeMs: number;
  estFeeBreakdown: WithdrawQuoteFeeBreakdown;
  estInputUsd: number;
  estOutputUsd: number;
  /** 预计到账数量（目标币最小单位，字符串） */
  estToTokenBaseUnit: string;
  quoteId: string;
}

interface WithdrawQuoteRequest {
  /** 提现金额（USDC.e 最小单位） */
  fromAmountBaseUnit: string;
  fromChainId: string;
  fromTokenAddress: string;
  toChainId: string;
  toTokenAddress: string;
  recipientAddress: string;
}

export async function fetchWithdrawQuote(
  params: WithdrawQuoteRequest,
): Promise<WithdrawQuoteResponse> {
  const res = await fetch(QUOTE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json || typeof json.error === 'string') {
    throw new Error(json?.error ?? `quote request failed: ${res.status}`);
  }
  return json as WithdrawQuoteResponse;
}

/** 输入防抖，避免用户边输入金额/地址边请求报价 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export interface UseWithdrawQuoteOptions {
  /** 提现金额（USDC） */
  amount?: number;
  /** 收款地址 */
  recipientAddress?: string;
  /** 目标链 id，如 137 / '137' */
  toChainId?: string | number;
  /** 目标币合约地址；默认与来源相同（Polygon USDC.e） */
  toTokenAddress?: string;
  /** 目标币精度；默认 6 */
  toTokenDecimals?: number;
  enabled?: boolean;
  debounceMs?: number;
  swr?: SWRConfiguration<WithdrawQuoteResponse, Error>;
}

/**
 * 提现报价：输入齐全（金额 > 0 且地址格式校验通过）时自动请求，输入变化防抖后重新报价。
 * 返回网络费（美元）与预计/最低到账数量（目标币单位）。
 */
export function useWithdrawQuote(options: UseWithdrawQuoteOptions = {}) {
  const {
    amount = 0,
    recipientAddress = '',
    toChainId = WITHDRAW_FROM_CHAIN_ID,
    toTokenAddress = WITHDRAW_FROM_TOKEN_ADDRESS,
    toTokenDecimals = WITHDRAW_FROM_TOKEN_DECIMALS,
    enabled = true,
    debounceMs = 500,
    swr,
  } = options;

  const address = recipientAddress.trim();
  // 地址格式校验通过后才发起报价请求
  const ready =
    enabled &&
    Number.isFinite(amount) &&
    amount > 0 &&
    isValidRecipientAddress(address, toChainId);

  // 请求参数序列化进 key，防抖后 fetcher 从 key 反解，保证 key 与请求一致
  const request: WithdrawQuoteRequest | null = ready
    ? {
        fromAmountBaseUnit: String(Math.round(amount * 10 ** WITHDRAW_FROM_TOKEN_DECIMALS)),
        fromChainId: WITHDRAW_FROM_CHAIN_ID,
        fromTokenAddress: WITHDRAW_FROM_TOKEN_ADDRESS,
        toChainId: String(toChainId),
        toTokenAddress,
        recipientAddress: address,
      }
    : null;
  const serialized = useDebouncedValue(request && JSON.stringify(request), debounceMs);

  const { data, error, isLoading, isValidating, mutate } = useSWR<WithdrawQuoteResponse, Error>(
    serialized ? (['polymarket-withdraw-quote', serialized] as const) : null,
    ([, body]: readonly [string, string]) => fetchWithdrawQuote(JSON.parse(body)),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      dedupingInterval: 15_000,
      ...swr,
    },
  );

  const receiveAmount = data ? Number(data.estToTokenBaseUnit) / 10 ** toTokenDecimals : null;
  console.log(isLoading, isValidating);
  return {
    quote: data ?? null,
    /** 网络费（美元） */
    networkFeeUsd: data?.estFeeBreakdown.gasUsd ?? null,
    /** 预计到账数量（目标币单位） */
    receiveAmount,
    /** 扣除滑点后的最低到账数量（目标币单位） */
    minReceived: data?.estFeeBreakdown.minReceived ?? null,
    /** 预计到账耗时（毫秒） */
    estCheckoutTimeMs: data?.estCheckoutTimeMs ?? null,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWithdrawQuote;
